import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, Trash2, Eye, Package, Plus } from "lucide-react";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { usePermission } from "@/hooks/usePermission";
import { salesAPI, getApiErrorPayload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { getDashboardProductImageSrc } from "@/lib/productImageUrl";
import { Loading } from "@/components/ui/loader";
import { SplitBillSummaryCard, type SplitBillShare } from "@/features/sales/components/SplitBillSummaryCard";
import { confirmAction } from "@/components/ui/confirm-dialog";
import { formatDate } from "@/lib/utils";

interface SalesItem {
	id: string;
	productId: string;
	productName: string;
	productImage?: string;
	product?: { name?: string; image?: string };
	quantity: number;
	unitPrice: number;
	discountPercentage?: number;
	discountAmount: number;
	taxPercentage?: number;
	taxRate?: number;
	taxAmount: number;
	totalAmount: number;
}

interface SalesTransaction {
	id: string;
	invoiceNumber: string;
	customerId: string;
	customer?: {
		id: string;
		firstName?: string;
		lastName?: string;
		companyName?: string;
		email?: string;
		phone?: string;
		address?: string;
	};
	items: SalesItem[];
	subtotal: number;
	discountAmount: number;
	taxAmount: number;
	shippingAmount: number;
	totalAmount: number;
	paidAmount: number;
	remainingAmount: number;
	paymentStatus: "pending" | "partial" | "paid";
	paymentMethod?: "cash" | "card" | "bank_transfer" | "check" | "credit";
	status: "pending" | "confirmed" | "shipped" | "delivered" | "cancelled";
	notes: string;
	tableNumber?: string | null;
	saleDate: string;
	dueDate: string;
	createdBy: { firstName: string; id: number };
	createdAt: string;
	updatedAt: string;
	splitStatus?: string | null;
	splitChildren?: SplitBillShare[];
}

interface SaleUpdatePayload {
	status?: "pending" | "confirmed" | "shipped" | "delivered" | "cancelled";
	paymentStatus?: "pending" | "partial" | "paid" | "overdue";
	paymentMethod?: "cash" | "card" | "bank_transfer" | "check" | "credit";
	subtotal?: number;
	taxAmount?: number;
	discountAmount?: number;
	shippingAmount?: number;
	totalAmount?: number;
	paidAmount?: number;
	remainingAmount?: number;
	dueDate?: string;
	notes?: string;
	customerId?: number;
	items: Array<{
		productId: number;
		quantity: number;
		unitPrice: number;
		discountRate?: number;
		taxRate?: number;
	}>;
}

const SalesDetailPage: React.FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const currency = useCurrency();
	const canEdit = usePermission("sales.update");
	const isViewOnly = !!id && !canEdit;
	const [addingPayment, setAddingPayment] = useState(false);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [salesTransaction, setSalesTransaction] = useState<SalesTransaction | null>(null);
	const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
	const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
	const [paymentForm, setPaymentForm] = useState({
		amount: 0,
		paymentMethod: "cash" as "cash" | "card" | "bank_transfer" | "check" | "credit",
		notes: "",
	});
	const [pdfLoading, setPdfLoading] = useState(false);

	useEffect(() => {
		if (id) {
			fetchSalesTransaction();
			fetchPayments();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on id change only
	}, [id]);

	const fetchSalesTransaction = async () => {
		try {
			setLoading(true);
			const data = await salesAPI.getById(id!);
			setSalesTransaction(data);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("sales.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/sales");
		} finally {
			setLoading(false);
		}
	};

	const fetchPayments = async () => {
		try {
			if (id) {
				const data = await salesAPI.getPayments(id);
				setPaymentHistory(data || []);
			}
		} catch (error) {
			console.error("Failed to fetch payments:", error);
			// If endpoint doesn't exist yet, use empty array
			setPaymentHistory([]);
		}
	};

	const handleAddPayment = async () => {
		if (!id || !salesTransaction || addingPayment) return;

		const paymentAmount = Number(paymentForm.amount);
		const currentPaid = Number(salesTransaction.paidAmount) || 0;
		const currentRemaining = Number(salesTransaction.remainingAmount) || 0;

		if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
			toast({
				title: t("common.error"),
				description: t("sales.errors.invalidPaymentAmount"),
				variant: "destructive",
			});
			return;
		}

		if (paymentAmount > currentRemaining) {
			toast({
				title: t("common.error"),
				description: t("sales.errors.paymentExceedsRemaining"),
				variant: "destructive",
			});
			return;
		}

		setAddingPayment(true);
		try {
			const updatedSale = await salesAPI.addPayment(id, {
				...paymentForm,
				amount: paymentAmount,
			});

			setSalesTransaction(prev =>
				prev
					? {
							...prev,
							paidAmount: Number(updatedSale.paidAmount ?? currentPaid + paymentAmount),
							remainingAmount: Number(updatedSale.remainingAmount ?? currentRemaining - paymentAmount),
							paymentStatus: (updatedSale.paymentStatus ??
								prev.paymentStatus) as SalesTransaction["paymentStatus"],
						}
					: null,
			);

			await fetchPayments();

			setPaymentForm({ amount: 0, paymentMethod: "cash", notes: "" });
			setIsPaymentDialogOpen(false);

			toast({
				title: t("common.success"),
				description: t("sales.messages.paymentAdded"),
			});
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("sales.errors.paymentFailed"),
				variant: "destructive",
			});
		} finally {
			setAddingPayment(false);
		}
	};

	const handleInputChange = (field: keyof SalesTransaction, value: any) => {
		if (salesTransaction) {
			setSalesTransaction(prev => ({ ...prev!, [field]: value }));
		}
	};

	const buildSaleUpdatePayload = (value: SalesTransaction): SaleUpdatePayload => {
		return {
			status: value.status,
			paymentStatus: value.paymentStatus,
			paymentMethod: value.paymentMethod,
			subtotal: Number(value.subtotal) || 0,
			taxAmount: Number(value.taxAmount) || 0,
			discountAmount: Number(value.discountAmount) || 0,
			shippingAmount: Number(value.shippingAmount) || 0,
			totalAmount: Number(value.totalAmount) || 0,
			paidAmount: Number(value.paidAmount) || 0,
			remainingAmount: Number(value.remainingAmount) || 0,
			dueDate: value.dueDate || undefined,
			notes: value.notes || undefined,
			customerId: value.customerId ? Number(value.customerId) : undefined,
			items: (value.items || []).map(item => ({
				productId: Number(item.productId),
				quantity: Number(item.quantity) || 0,
				unitPrice: Number(item.unitPrice) || 0,
				discountRate: Number(item.discountPercentage ?? 0) || 0,
				taxRate: Number(item.taxPercentage ?? item.taxRate ?? 0) || 0,
			})),
		};
	};

	const handleItemChange = (itemId: string, field: keyof SalesItem, value: any) => {
		if (salesTransaction) {
			const updatedItems = salesTransaction.items.map(item => {
				if (item.id === itemId) {
					const updatedItem = { ...item, [field]: value };
					const discountPct = Number(updatedItem.discountPercentage) || 0;
					const taxPct = Number(updatedItem.taxPercentage ?? updatedItem.taxRate) || 0;

					// Recalculate amounts
					const discountAmount = (updatedItem.unitPrice * updatedItem.quantity * discountPct) / 100;
					const taxAmount = ((updatedItem.unitPrice * updatedItem.quantity - discountAmount) * taxPct) / 100;
					const totalAmount = updatedItem.unitPrice * updatedItem.quantity - discountAmount + taxAmount;

					return {
						...updatedItem,
						discountAmount,
						taxAmount,
						totalAmount,
					};
				}
				return item;
			});

			// Recalculate totals
			const subtotal = updatedItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
			const totalDiscount = updatedItems.reduce((sum, item) => sum + item.discountAmount, 0);
			const totalTax = updatedItems.reduce((sum, item) => sum + item.taxAmount, 0);
			const totalAmount = subtotal - totalDiscount + totalTax + (salesTransaction.shippingAmount ?? 0);

			setSalesTransaction(prev => ({
				...prev!,
				items: updatedItems,
				subtotal,
				discountAmount: totalDiscount,
				taxAmount: totalTax,
				totalAmount,
			}));
		}
	};

	const handleSubmit = async () => {
		try {
			setSaving(true);
			if (salesTransaction) {
				await salesAPI.update(id!, buildSaleUpdatePayload(salesTransaction));
				toast({
					title: t("common.success"),
					description: t("sales.messages.updated"),
				});
			}
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (await confirmAction(t("sales.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await salesAPI.delete(id!);
				toast({
					title: t("common.success"),
					description: t("sales.messages.deleted"),
				});
				navigate("/sales");
			} catch (error) {
				const payload = getApiErrorPayload(error);
				const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
				toast({
					title: t("common.error"),
					description: errorMessage || t("common.errors.unexpected"),
					variant: "destructive",
				});
			}
		}
	};

	const generatePDF = async () => {
		if (!id) return;
		try {
			setPdfLoading(true);
			const blob = await salesAPI.downloadPdf(id);
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `invoice-${salesTransaction?.invoiceNumber ?? id}.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
			toast({
				title: t("common.success"),
				description: t("sales.messages.pdfGenerated"),
			});
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description:
					t(payload.key, (payload.params ?? {}) as Record<string, string>) ||
					t("sales.errors.pdfGenerationFailed"),
				variant: "destructive",
			});
		} finally {
			setPdfLoading(false);
		}
	};

	const getStatusBadge = (status: string) => {
		const statusConfig: Record<string, { variant: string; text: string }> = {
			pending: { variant: "warning", text: t("sales.statuses.pending") },
			confirmed: { variant: "success", text: t("sales.statuses.confirmed") },
			shipped: { variant: "default", text: t("sales.statuses.shipped") },
			cancelled: { variant: "destructive", text: t("sales.statuses.cancelled") },
			refunded: { variant: "default", text: t("sales.statuses.refunded") },
		};

		const config = statusConfig[status] || { variant: "default", text: status };
		return <Badge variant={config.variant as any}>{config.text}</Badge>;
	};

	const getPaymentStatusBadge = (status: string) => {
		const statusConfig: Record<string, { variant: string; text: string }> = {
			pending: { variant: "warning", text: t("sales.paymentStatuses.pending") },
			partial: { variant: "default", text: t("sales.paymentStatuses.partial") },
			paid: { variant: "success", text: t("sales.paymentStatuses.paid") },
			overdue: { variant: "destructive", text: t("sales.paymentStatuses.overdue") },
		};

		const config = statusConfig[status] || { variant: "default", text: status };
		return <Badge variant={config.variant as any}>{config.text}</Badge>;
	};

	const isConfirmed = salesTransaction?.status === "confirmed";
	const splitChildren = salesTransaction?.splitChildren ?? [];
	const isSplitParent = salesTransaction?.splitStatus === "split" && splitChildren.length > 0;
	const displayPaymentStatus = isSplitParent
		? splitChildren.every(share => share.paymentStatus === "paid")
			? "paid"
			: splitChildren.some(share => Number(share.paidAmount) > 0)
				? "partial"
				: "pending"
		: (salesTransaction?.paymentStatus ?? "pending");

	if (loading) return <Loading />;

	if (!salesTransaction) {
		return (
			<div className="text-center py-8">
				<p className="text-muted-foreground">{t("sales.errors.notFound")}</p>
			</div>
		);
	}

	return (
		<div className="container mx-auto max-w-full space-y-4 px-3 py-4 md:px-4 md:py-5">
			<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">
							{isViewOnly ? t("common.view") : t("sales.detail.title")}
						</PageHeading>
						<p className="text-muted-foreground">
							{isViewOnly
								? t("sales.detail.viewDescription")
								: `${t("sales.detail.description")} #${salesTransaction.invoiceNumber}`}
						</p>
						{salesTransaction.tableNumber ? (
							<p className="mt-2">
								<Badge variant="secondary">
									{t("sales.detail.tableNumber")}: {salesTransaction.tableNumber}
								</Badge>
							</p>
						) : null}
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{!isSplitParent ? (
						<Button variant="outline" onClick={generatePDF} disabled={pdfLoading}>
							<Download className="h-4 w-4 mx-2" />
							{pdfLoading ? t("common.loading") : t("sales.actions.downloadPDF")}
						</Button>
					) : null}
					{!isViewOnly && !isSplitParent && (
						<PermissionGate permission="sales.delete">
							<Button variant="destructive" onClick={handleDelete}>
								<Trash2 className="h-4 w-4 mx-2" />
								{t("common.delete")}
							</Button>
						</PermissionGate>
					)}
				</div>
			</div>

			<div className="space-y-4">
				{isConfirmed && !isSplitParent && (
					<p className="text-sm text-warning rounded-lg bg-warning/10 p-3">
						{t(
							"sales.detail.confirmedReadOnly",
							"Confirmed sales cannot be edited. You can add payments in the Payments tab.",
						)}
					</p>
				)}
				<Card>
					<CardHeader>
						<CardTitle className="flex flex-wrap items-center justify-between gap-3">
							{t("sales.detail.invoiceInfo")}
							<div className="flex flex-wrap items-center gap-x-6 gap-y-3">
								<div className="flex items-center gap-2">
									<Label>{t("sales.detail.status")}</Label>
									{getStatusBadge(salesTransaction.status)}
								</div>
								<div className="flex items-center gap-2">
									<Label>{t("sales.detail.paymentStatus")}</Label>
									{getPaymentStatusBadge(displayPaymentStatus)}
								</div>
							</div>
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
							<div className="space-y-2">
								<Label>{t("sales.detail.invoiceNumber")}</Label>
								<Input
									aria-label={t("sales.detail.invoiceNumber")}
									value={salesTransaction.invoiceNumber}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("sales.detail.saleDate")}</Label>
								<Input
									aria-label={t("sales.detail.saleDate")}
									type="date"
									value={
										salesTransaction.saleDate
											? String(salesTransaction.saleDate).slice(0, 10)
											: salesTransaction.createdAt
												? new Date(salesTransaction.createdAt).toISOString().slice(0, 10)
												: ""
									}
									onChange={e => handleInputChange("saleDate", e.target.value)}
									readOnly={isConfirmed || isViewOnly}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("sales.detail.invoiceIssuer")}</Label>
								<Input
									aria-label={t("sales.detail.invoiceIssuer")}
									value={
										`${salesTransaction?.createdBy?.firstName ?? ""} (${salesTransaction?.createdBy?.id ?? ""})`.trim() ||
										t("sales.detail.unknownEmployee")
									}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("sales.detail.dueDate")}</Label>
								<Input
									aria-label={t("sales.detail.dueDate")}
									type="date"
									value={
										salesTransaction.dueDate ? String(salesTransaction.dueDate).slice(0, 10) : ""
									}
									onChange={e => handleInputChange("dueDate", e.target.value)}
									readOnly={isConfirmed || isViewOnly}
								/>
							</div>
						</div>

						<div className="space-y-2">
							<Label>{t("sales.detail.notes")}</Label>
							<Input
								aria-label={t("sales.detail.notes")}
								type="text"
								value={salesTransaction.notes ?? ""}
								onChange={e => handleInputChange("notes", e.target.value)}
								readOnly={isConfirmed || isViewOnly}
							/>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>{t("sales.detail.items")}</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{salesTransaction.items.map(item => (
								<div key={item.id} className="flex items-center gap-4 p-4 border rounded-lg">
									<div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center overflow-hidden">
										{getDashboardProductImageSrc(item.product?.image ?? item.productImage) ? (
											<img
												src={getDashboardProductImageSrc(
													item.product?.image ?? item.productImage,
												)}
												alt={item.product?.name ?? item.productName}
												className="w-full h-full object-cover rounded-lg"
											/>
										) : (
											<Package className="h-8 w-8 text-primary" />
										)}
									</div>
									<div className="flex-1">
										<h4 className="font-medium">
											{item.product?.name ?? item.productName ?? `#${item.productId}`}
										</h4>
										<div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-2">
											<div>
												<Label className="text-sm">{t("sales.detail.quantity")}</Label>
												<Input
													aria-label={t("sales.detail.quantity")}
													type="number"
													min="1"
													value={item.quantity}
													onChange={e =>
														handleItemChange(
															item.id,
															"quantity",
															parseInt(e.target.value) || 1,
														)
													}
													className="w-20"
													readOnly={isConfirmed || isViewOnly}
												/>
											</div>
											<div>
												<Label className="text-sm">{t("sales.detail.unitPrice")}</Label>
												<Input
													aria-label={t("sales.detail.unitPrice")}
													type="number"
													min="0"
													step="0.01"
													value={item.unitPrice}
													onChange={e =>
														handleItemChange(
															item.id,
															"unitPrice",
															parseFloat(e.target.value) || 0,
														)
													}
													className="w-24"
													readOnly={isConfirmed || isViewOnly}
												/>
											</div>
											<div>
												<Label className="text-sm">{t("sales.detail.discount")} </Label>
												<Input
													aria-label={t("sales.detail.discount")}
													type="number"
													min="0"
													max="100"
													step="0.01"
													value={
														item.discountPercentage ??
														(item as { discountRate?: number }).discountRate ??
														0
													}
													onChange={e =>
														handleItemChange(
															item.id,
															"discountPercentage",
															parseFloat(e.target.value) || 0,
														)
													}
													className="w-20"
													readOnly={isConfirmed || isViewOnly}
												/>
											</div>
											<div>
												<Label className="text-sm">{t("sales.add.taxPercent")} </Label>
												<Input
													aria-label={t("sales.add.taxPercent")}
													type="number"
													min="0"
													max="100"
													step="0.01"
													value={item.taxPercentage ?? item.taxRate ?? 0}
													onChange={e =>
														handleItemChange(
															item.id,
															"taxPercentage",
															parseFloat(e.target.value) || 0,
														)
													}
													className="w-20"
													readOnly={isConfirmed || isViewOnly}
												/>
											</div>
											<div>
												<Label className="text-sm">{t("sales.detail.total")}</Label>
												<div className="text-lg font-semibold">
													<Price value={item.totalAmount} currency={currency} />
												</div>
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>{t("sales.detail.summary")}</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							<div className="flex justify-between">
								<span>{t("sales.detail.subtotal")}</span>
								<span className="font-semibold">
									<Price value={salesTransaction.subtotal} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("sales.detail.discount")}</span>
								<span className="font-semibold text-destructive">
									-<Price value={salesTransaction.discountAmount} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("sales.detail.tax")}</span>
								<span className="font-semibold">
									<Price value={salesTransaction.taxAmount} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("sales.detail.shipping")}</span>
								<span className="font-semibold">
									<Price value={salesTransaction.shippingAmount ?? 0} currency={currency} />
								</span>
							</div>
							<div className="border-t pt-4">
								<div className="flex justify-between text-lg font-bold">
									<span>{t("sales.detail.total")}</span>
									<span>
										<Price value={salesTransaction.totalAmount} currency={currency} />
									</span>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<CardTitle>{t("sales.detail.customerInfo")}</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							<div className="space-y-2">
								<Label>{t("sales.detail.customerName")}</Label>
								<Input
									aria-label={t("sales.detail.customerName")}
									value={
										[salesTransaction.customer?.firstName, salesTransaction.customer?.lastName]
											.filter(Boolean)
											.join(" ") ||
										salesTransaction.customer?.companyName ||
										`#${salesTransaction.customer?.id ?? ""}`
									}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("sales.detail.customerEmail")}</Label>
								<Input
									aria-label={t("sales.detail.customerEmail")}
									value={salesTransaction.customer?.email}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("sales.detail.customerPhone")}</Label>
								<Input
									aria-label={t("sales.detail.customerPhone")}
									value={salesTransaction.customer?.phone}
									readOnly
								/>
							</div>
							<div className="space-y-2 col-span-3">
								<Label>{t("sales.detail.customerAddress")}</Label>
								<Input
									aria-label={t("sales.detail.customerAddress")}
									value={salesTransaction.customer?.address}
									readOnly
								/>
							</div>
						</div>
					</CardContent>
				</Card>
				{isSplitParent ? (
					<SplitBillSummaryCard
						shares={splitChildren}
						currency={currency}
						onOpenShare={shareId => navigate(`/sales/${shareId}`)}
					/>
				) : (
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center justify-between">
								{t("sales.detail.payments")}
								{!isViewOnly && (
									<Button
										variant="outline"
										size="sm"
										onClick={() => {
											const remaining = Number(salesTransaction?.remainingAmount) || 0;
											if (salesTransaction && remaining > 0) {
												setPaymentForm({
													amount: remaining,
													paymentMethod: "cash",
													notes: "",
												});
												setIsPaymentDialogOpen(true);
											} else {
												toast({
													title: t("common.error"),
													description: t("sales.errors.noRemainingAmount"),
													variant: "destructive",
												});
											}
										}}
										disabled={
											!salesTransaction || (Number(salesTransaction.remainingAmount) || 0) <= 0
										}
									>
										<Plus className="h-4 w-4 mx-2" />
										{t("sales.actions.createPayment")}
									</Button>
								)}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="space-y-4">
								<div className="flex justify-between items-center p-4 bg-muted rounded-lg">
									<div>
										<p className="font-medium">{t("sales.detail.totalAmount")}</p>
										<p className="text-xl font-bold">
											<Price
												value={Number(salesTransaction.totalAmount) || 0}
												currency={currency}
											/>
										</p>
									</div>
									<div>
										<p className="font-medium">{t("sales.detail.paidAmount")}</p>
										<p className="text-xl font-bold text-success">
											<Price
												value={Number(salesTransaction.paidAmount) || 0}
												currency={currency}
											/>
										</p>
									</div>
									<div>
										<p className="font-medium">{t("sales.detail.remainingAmount")}</p>
										<p className="text-xl font-bold text-destructive">
											<Price
												value={Number(salesTransaction.remainingAmount) || 0}
												currency={currency}
											/>
										</p>
									</div>
								</div>

								<div className="space-y-2">
									<Label>{t("sales.detail.paymentHistory")}</Label>
									<div className="space-y-2">
										{paymentHistory.length > 0 ? (
											paymentHistory.map((payment: any, index: number) => (
												<div key={payment.id || index} className="p-4 border rounded-lg">
													<div className="flex justify-between items-center mb-2">
														<div>
															<p className="font-medium">
																{formatDate(
																	payment.createdAt ||
																		payment.paymentDate ||
																		salesTransaction?.createdAt,
																)}
															</p>
															<p className="text-sm text-muted-foreground">
																{t(
																	`sales.paymentMethods.${payment.paymentMethod || salesTransaction?.paymentMethod || "cash"}`,
																)}
															</p>
															{payment.notes && (
																<p className="text-xs text-muted-foreground mt-1">
																	{payment.notes}
																</p>
															)}
														</div>
														<div className="text-start">
															<p className="font-semibold">
																<Price
																	value={
																		Number(payment.amount ?? payment.paidAmount) ||
																		0
																	}
																	currency={currency}
																/>
															</p>
															<Badge variant="default">
																{t("sales.paymentStatuses.paid")}
															</Badge>
														</div>
													</div>
												</div>
											))
										) : salesTransaction && salesTransaction.paidAmount > 0 ? (
											<div className="p-4 border rounded-lg">
												<div className="flex justify-between items-center mb-2">
													<div>
														<p className="font-medium">
															{formatDate(salesTransaction.createdAt)}
														</p>
														<p className="text-sm text-muted-foreground">
															{t(
																`sales.paymentMethods.${salesTransaction.paymentMethod || "cash"}`,
															)}
														</p>
													</div>
													<div className="text-start">
														<p className="font-semibold">
															<Price
																value={Number(salesTransaction.paidAmount) || 0}
																currency={currency}
															/>
														</p>
														{getPaymentStatusBadge(salesTransaction.paymentStatus)}
													</div>
												</div>
												{salesTransaction.remainingAmount > 0 && (
													<div className="mt-2 pt-2 border-t">
														<p className="text-sm text-muted-foreground">
															{t("sales.detail.remainingAmount")}:{" "}
															<span className="font-medium text-destructive">
																<Price
																	value={
																		Number(salesTransaction.remainingAmount) || 0
																	}
																	currency={currency}
																/>
															</span>
														</p>
													</div>
												)}
											</div>
										) : (
											<div className="p-4 border rounded-lg text-center text-muted-foreground">
												{t("sales.detail.noPaymentHistory")}
											</div>
										)}
									</div>
								</div>
							</div>
						</CardContent>
					</Card>
				)}
			</div>

			{!isConfirmed && !isViewOnly && (
				<div className="flex justify-end">
					<Button onClick={handleSubmit} disabled={saving} className="px-12">
						{saving ? (
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
						) : (
							<Eye className="h-4 w-4 mx-2" />
						)}
						{t("common.save")}
					</Button>
				</div>
			)}

			{/* Payment Dialog */}
			<Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("sales.actions.createPayment")}</DialogTitle>
						<DialogDescription>{t("sales.detail.createPaymentDescription")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="paymentAmount">{t("sales.detail.paymentAmount")}</Label>
							<Input
								id="paymentAmount"
								type="number"
								min="0"
								max={Number(salesTransaction?.remainingAmount) || 0}
								step="0.01"
								value={paymentForm.amount}
								onChange={e =>
									setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })
								}
								placeholder={t("sales.detail.paymentAmountPlaceholder")}
							/>
							{salesTransaction && (
								<p className="text-sm text-muted-foreground">
									{t("sales.detail.remainingAmount")}:{" "}
									<span className="font-medium">
										<Price
											value={Number(salesTransaction.remainingAmount) || 0}
											currency={currency}
										/>
									</span>
								</p>
							)}
						</div>
						<div className="space-y-2">
							<Label htmlFor="paymentMethod">{t("sales.detail.paymentMethod")}</Label>
							<Select
								value={paymentForm.paymentMethod}
								onValueChange={(value: "cash" | "card" | "bank_transfer" | "check" | "credit") =>
									setPaymentForm({ ...paymentForm, paymentMethod: value })
								}
							>
								<SelectTrigger aria-label={t("sales.detail.paymentMethod")}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="cash">{t("sales.paymentMethods.cash")}</SelectItem>
									<SelectItem value="card">{t("sales.paymentMethods.card")}</SelectItem>
									<SelectItem value="bank_transfer">
										{t("sales.paymentMethods.bank_transfer")}
									</SelectItem>
									<SelectItem value="check">{t("sales.paymentMethods.check")}</SelectItem>
									<SelectItem value="credit">{t("sales.paymentMethods.credit")}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="paymentNotes">{t("sales.detail.paymentNotes")}</Label>
							<Input
								id="paymentNotes"
								type="text"
								value={paymentForm.notes}
								onChange={e => setPaymentForm({ ...paymentForm, notes: e.target.value })}
								placeholder={t("sales.detail.paymentNotesPlaceholder")}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleAddPayment} disabled={paymentForm.amount <= 0 || addingPayment}>
							{t("sales.actions.addPayment")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default SalesDetailPage;
